import { Check, Eye, EyeOff } from "lucide-react";
import { useState } from "react";
import { getPasswordRequirements, PASSWORD_MAX_LENGTH } from "../lib/password";

export function PasswordField({
  value,
  onChange,
  placeholder,
  autoComplete,
  required = false,
  minLength,
  id,
  showRequirements = false,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  autoComplete?: string;
  required?: boolean;
  minLength?: number;
  id?: string;
  showRequirements?: boolean;
}) {
  const [visible, setVisible] = useState(false);
  const requirements = getPasswordRequirements(value);
  const requirementsId = id ? `${id}-requirements` : undefined;
  const boundedValue = value.slice(0, PASSWORD_MAX_LENGTH);

  return (
    <div className={showRequirements ? "space-y-2" : undefined}>
      <div className="relative">
        <input
          id={id}
          required={required}
          type={visible ? "text" : "password"}
          className="field pr-11"
          placeholder={placeholder}
          value={boundedValue}
          onChange={(e) => onChange(e.target.value.slice(0, PASSWORD_MAX_LENGTH))}
          autoComplete={autoComplete}
          minLength={minLength}
          maxLength={PASSWORD_MAX_LENGTH}
          aria-describedby={requirementsId}
        />
        <button
          type="button"
          onClick={() => setVisible((current) => !current)}
          aria-label={visible ? "Hide password" : "Show password"}
          className="absolute inset-y-0 right-0 px-3 flex items-center text-mut hover:text-navy"
        >
          {visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
        </button>
      </div>
      {showRequirements && (
        <div id={requirementsId} className="text-xs text-mut" aria-live="polite">
          <p className="font-semibold text-navy mb-1">Password requirements</p>
          <ul className="space-y-1">
            {requirements.map((requirement) => (
              <li key={requirement.key} className={`flex w-full items-center gap-1.5 ${requirement.met ? "text-brand-green" : "text-mut"}`}>
                <Check className={`w-3.5 h-3.5 shrink-0 ${requirement.met ? "opacity-100" : "opacity-30"}`} aria-hidden="true" />
                {requirement.label}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
